import { Type, AlignLeft, Image as ImageIcon, Video, Music, FileText, Link2, ChevronUp, ChevronDown, Trash2 } from "lucide-react";
import FileUpload from "./FileUpload";
import { inputCls } from "@/pages/admin/ui";

const TYPES = [
  { type: "heading", label: "Titolo", icon: Type },
  { type: "text", label: "Testo", icon: AlignLeft },
  { type: "image", label: "Immagine", icon: ImageIcon },
  { type: "video", label: "Video", icon: Video },
  { type: "audio", label: "Audio", icon: Music },
  { type: "file", label: "File/PDF", icon: FileText },
  { type: "button", label: "Pulsante", icon: Link2 },
];

const DEFAULTS = {
  heading: { text: "" },
  text: { text: "" },
  image: { path: null, caption: "" },
  video: { media_kind: "youtube", url: "", path: null },
  audio: { cover_path: null, title: "", subtitle: "", path: null },
  file: { path: null, label: "" },
  button: { label: "", url: "" },
};

const uid = () => (crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

export default function BlockEditor({ blocks = [], onChange, testid = "blocks" }) {
  const list = Array.isArray(blocks) ? blocks : [];
  const add = (type) => onChange([...list, { id: uid(), type, ...DEFAULTS[type] }]);
  const upd = (i, patch) => onChange(list.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const copy = [...list];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2 mb-4">
        {TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.type} type="button" onClick={() => add(t.type)} data-testid={`${testid}-add-${t.type}`}
              className="inline-flex items-center gap-1.5 text-xs text-[#C6899E] border border-[rgba(198,137,158,0.4)] rounded-full px-3 py-1.5 hover:bg-[rgba(198,137,158,0.12)] transition-colors">
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="space-y-3">
        {list.map((b, i) => (
          <div key={b.id} className="rounded-xl border border-[rgba(61,50,56,0.12)] bg-[#FBF5F1] p-3" data-testid={`${testid}-block-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[0.65rem] uppercase tracking-[0.12em] text-[#9C8F94]">{b.type}</span>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} className="p-1 text-[#9C8F94] hover:text-[#C6899E]"><ChevronUp className="w-4 h-4" /></button>
                <button type="button" onClick={() => move(i, 1)} className="p-1 text-[#9C8F94] hover:text-[#C6899E]"><ChevronDown className="w-4 h-4" /></button>
                <button type="button" onClick={() => remove(i)} className="p-1 text-[#9C8F94] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            {b.type === "heading" && <input className={inputCls} placeholder="Titolo" value={b.text} onChange={(e) => upd(i, { text: e.target.value })} />}
            {b.type === "text" && <textarea rows={4} className={inputCls} placeholder="Testo (righe vuote = nuovi paragrafi)" value={b.text} onChange={(e) => upd(i, { text: e.target.value })} />}
            {b.type === "image" && (
              <div className="space-y-2">
                <FileUpload accept="image/*" value={b.path} onChange={(p) => upd(i, { path: p })} />
                <input className={inputCls} placeholder="Didascalia (opzionale)" value={b.caption} onChange={(e) => upd(i, { caption: e.target.value })} />
              </div>
            )}
            {b.type === "video" && (
              <div className="space-y-2">
                <select className={inputCls} value={b.media_kind} onChange={(e) => upd(i, { media_kind: e.target.value })}>
                  <option value="youtube">YouTube</option><option value="vimeo">Vimeo</option><option value="upload">Upload</option>
                </select>
                {b.media_kind === "upload"
                  ? <FileUpload accept="video/*" value={b.path} onChange={(p) => upd(i, { path: p })} />
                  : <input className={inputCls} placeholder="URL video" value={b.url} onChange={(e) => upd(i, { url: e.target.value })} />}
              </div>
            )}
            {b.type === "audio" && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input className={inputCls} placeholder="Titolo" value={b.title} onChange={(e) => upd(i, { title: e.target.value })} />
                  <input className={inputCls} placeholder="Sottotitolo (opzionale)" value={b.subtitle} onChange={(e) => upd(i, { subtitle: e.target.value })} />
                </div>
                <label className="block text-xs text-[#9C8F94]">Copertina</label>
                <FileUpload accept="image/*" value={b.cover_path} onChange={(p) => upd(i, { cover_path: p })} />
                <label className="block text-xs text-[#9C8F94]">File audio (scaricabile)</label>
                <FileUpload accept="audio/*" value={b.path} onChange={(p) => upd(i, { path: p })} />
              </div>
            )}
            {b.type === "file" && (
              <div className="space-y-2">
                <FileUpload accept="*" value={b.path} onChange={(p) => upd(i, { path: p })} />
                <input className={inputCls} placeholder="Etichetta pulsante (es. Scarica il PDF)" value={b.label} onChange={(e) => upd(i, { label: e.target.value })} />
              </div>
            )}
            {b.type === "button" && (
              <div className="grid grid-cols-2 gap-2">
                <input className={inputCls} placeholder="Testo" value={b.label} onChange={(e) => upd(i, { label: e.target.value })} />
                <input className={inputCls} placeholder="URL" value={b.url} onChange={(e) => upd(i, { url: e.target.value })} />
              </div>
            )}
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-[#9C8F94] py-3">Nessun contenuto. Aggiungi un blocco qui sopra.</p>}
      </div>
    </div>
  );
}
